import { type FC, lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import type { Controls, EventData } from "react-joyride";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { AnnieWalkthrough } from "../../../utils/annieStream";
import { useWalkthroughLaunch } from "./useWalkthroughLaunch";
import { useWalkthroughStepAdvance } from "./useWalkthroughStepAdvance";
import { useWalkthroughTourGuide } from "./useWalkthroughTourGuide";
import { WalkthroughTooltip } from "./WalkthroughTooltip";
import {
	ANCHOR_ARRIVAL_MUTATIONS,
	dismissRevealedAnchors,
	isAnchorVisible,
	isDispatchingDismissKey,
	overlaySurfaceOf,
	watchAnchor,
} from "./walkthroughAnchors";
import { WalkthroughTooltipGuideContext } from "./walkthroughGuideContext";
import type { WalkthroughGuide } from "./walkthroughGuidePose";
import { WalkthroughNextOpensContext } from "./walkthroughNextOpensContext";
import { MISSING_TARGET_WAIT_MS, type WalkthroughStepData } from "./walkthroughSteps";

const Joyride = lazy(() => import("react-joyride").then((module) => ({ default: module.Joyride })));

type Props = {
	/** The walkthrough to run, or null when none is active. */
	walkthrough: AnnieWalkthrough | null;
	onFinish: () => void;
	/**
	 * Fires when the tour actually starts and again when it ends, for a host that has to yield the screen
	 * to it (Annie's panel and FAB sit under the overlay's spotlight and would show through it).
	 *
	 * Keyed on RUNNING, not on being asked: a refusal — wrong surface, unsaved changes, an unresolvable
	 * route, too few visible steps — never starts, and the last of those is only known after up to 3s of
	 * anchor polling. A host that hid on the request would hide for that whole wait and then reappear
	 * alongside a snackbar telling the user to read the answer it was covering.
	 *
	 * Pass a stable callback (a `useState` setter is ideal); it is called from an effect on the flag.
	 */
	onRunningChange?: (isRunning: boolean) => void;
	/** Decorative guide that walks the tour; pass a memoized object. */
	// DEFAULT: absent = the tour exactly as before, with no figure.
	guide?: WalkthroughGuide;
};

/**
 * Above the chat panel (`FloatingChatWindow` DEFAULT_Z_INDEX = 1300) and MUI's modal layer. Explicit
 * because the default 100 only wins by accident: the panel is `position: fixed` but not portaled, so
 * its 1300 is scoped to Layout's stacking context while joyride appends to document.body.
 */
const WALKTHROUGH_Z_INDEX = 1400;

/**
 * Whether a modal is actually OPEN, which is not the same as one being in the DOM.
 *
 * MUI keeps a `keepMounted` modal mounted with its root `visibility: hidden` (`Modal`'s
 * `!open && exited` variant), so matching the selector alone would hand Escape to a dialog nobody can
 * see — permanently, on any page that adopts `keepMounted`, leaving the tour with the ✕ as its only exit
 * and no failing test to say so.
 */
const MODAL_SURFACE_SELECTOR = ".MuiModal-root";

const hasOpenModal = (): boolean =>
	[...document.querySelectorAll(MODAL_SURFACE_SELECTOR)].some((modal) => window.getComputedStyle(modal).visibility !== "hidden");

/**
 * Runs a walkthrough: navigates into the state it needs, then steps through its anchors. Mounted beside
 * `AnniePanel` in the layout so it outlives the navigation it causes.
 *
 * The deciding — refuse or launch, navigate, snapshot the anchors, apply the step floor — lives in
 * `useWalkthroughLaunch`; what is left here is the joyride element, its telemetry, and the Escape key.
 *
 * react-joyride runs UNCONTROLLED: the docs reserve `stepIndex` for an external owner of the index and
 * warn that driving it from an effect desynchronizes the lifecycle, and uncontrolled auto-advances past
 * a target that isn't there — which is what we want for a control this department doesn't have.
 *
 * `blockTargetInteraction` defaults to false, so the spotlit control stays clickable through the cutout and
 * the user can open the thing with the tooltip still up, which is the path a `reveal: "interaction"` step's
 * copy is written for. Keeping them ON that step until they do is `WalkthroughTooltip`'s job, because
 * joyride's own fallback is not one: while it polls for a missing target the overlay renders with no cutout
 * and still swallows clicks, so the wait is a black screen the user cannot act on (measured at 3.03s before
 * `MISSING_TARGET_WAIT_MS` cut it). A step that is genuinely unreachable still emits `error:target_not_found`
 * (tracked below) and advances, which is what uncontrolled mode buys us.
 */
export const WalkthroughController: FC<Props> = ({ walkthrough, onFinish, onRunningChange, guide }) => {
	const { track, trackError } = useAnalytics();
	const { isRunning, steps, stop } = useWalkthroughLaunch(walkthrough, onFinish);
	// Which step joyride is showing. Reported on every `tooltip` event, so nothing new has to be wired
	// to know it; reset with the tour so a relaunch does not watch the previous run's last anchor.
	const [stepIndex, setStepIndex] = useState(0);
	// joyride hands these out only through its events; a relaunch is a new instance with its own.
	const [controls, setControls] = useState<Controls | null>(null);
	// Guards the between-steps unwind below against overlapping itself; see that effect.
	const dismissingRef = useRef(false);
	// Bumped when a deferred decision becomes answerable — see the unwind effect below.
	const [dismissRetry, setDismissRetry] = useState(0);
	useEffect(() => {
		if (!isRunning) {
			setStepIndex(0);
			setControls(null);
		}
	}, [isRunning]);
	const onNextOpens = useWalkthroughStepAdvance({ controls, steps, stepIndex });

	// The cleanup is the crash path: Layout's `ErrorBoundary` renders a null fallback, so a throw from the
	// lazily-imported joyride chunk unmounts this mid-tour with `isRunning` last reported TRUE — and
	// `concealed` then hides Annie's panel and her launcher with nothing left alive to clear the flag.
	// Only the running state needs undoing; an unconditional cleanup reports a false between every change.
	useEffect(() => {
		onRunningChange?.(isRunning);
		return isRunning ? () => onRunningChange?.(false) : undefined;
	}, [isRunning, onRunningChange]);

	// Escape belongs to the tour while one is running: the chat panel also closes on Escape and wins on
	// the bubble phase, leaving the overlay up with the answer being read gone.
	useEffect(() => {
		if (!isRunning) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape") return;
			// The tour dismisses a surface by DISPATCHING Escape (`dismissRevealedAnchors`), and this capture
			// listener sees those too. Reading one as the user asking to leave would end the tour every time it
			// tidied up after itself. `hasOpenModal` only covers it while the surface is a Modal — a persistent
			// drawer is not one — so this is what actually separates the two.
			if (isDispatchingDismissKey()) return;
			// A dialog opened through the spotlight owns Escape first, or the user can't dismiss it.
			if (hasOpenModal()) return;
			event.stopPropagation();
			stop();
		};
		document.addEventListener("keydown", onKeyDown, true);
		return () => document.removeEventListener("keydown", onKeyDown, true);
	}, [isRunning, stop]);

	/**
	 * End the tour when the control the CURRENT step describes is dismissed under it.
	 *
	 * joyride will not: `useSpotlightRect` watches for a target to appear, disconnects that observer the
	 * moment it resolves one, and afterwards recomputes only on scroll, window resize and a ResizeObserver
	 * bound to that element — none of which fire when it is removed. The step then renders against a stale
	 * rect: tooltip parked in the viewport corner (measured at 10,36), no cutout, still counting "Step 3 of
	 * 4" at a control that is gone, escapable only by pressing Escape twice because the first belongs to
	 * the thing being closed.
	 *
	 * Here rather than in the tooltip because this is a lifecycle decision, not a rendering one: it ends
	 * the tour through the same `stop` as every other exit, instead of routing a synthetic skip through
	 * joyride to arrive back at the same place. The index comes from the `tooltip` event below, which the
	 * step-shown telemetry already rides on.
	 */
	useEffect(() => {
		if (!isRunning || walkthrough === null) return;
		const data = steps[stepIndex]?.data as WalkthroughStepData | undefined;
		const anchor = data?.anchor;
		if (anchor === undefined) return;
		// A DEFERRED step's control is one the tour just talked the user into opening, and the most natural
		// thing to do with it is use it — the Call-Ins menu entry navigates and closes its own drawer. That
		// vanish is the user following the step, not the control being pulled out from under them, and
		// ending the tour on it strands them four steps in. `useWalkthroughStepAdvance` advances instead.
		if (data?.isDeferred === true) return;
		return watchAnchor(anchor, () => {
			// An opener hiding once it has opened its surface (the drawer button) is the step succeeding.
			if (typeof data?.revealAnchor === "string" && isAnchorVisible(data.revealAnchor)) return;
			track("annie_walkthrough_target_vanished", { topic_id: walkthrough.topicId, step: stepIndex, anchor });
			stop();
		});
	}, [isRunning, steps, stepIndex, walkthrough, stop, track]);

	/**
	 * Close a surface the tour opened as soon as it walks back OUT of it, not only when the tour ends.
	 *
	 * `stop` unwinds at the end, which is enough while every step after the reveal is inside the same
	 * panel — the shape every tour had until one needed to open a menu, point inside it, and then carry on
	 * with the page underneath. A drawer left open is not cosmetic there: the admin drawer is 340px of the
	 * right edge and covers the Call-Ins export button ENTIRELY, so a later step would spotlight a control
	 * the user cannot see, with joyride perfectly happy because the anchor is still in the DOM.
	 *
	 * Only surfaces the tour itself revealed are touched (`revealAnchor` of a step already passed), and only
	 * once the current step is somewhere else — see `overlaySurfaceOf` for why that comparison is on the
	 * surface and not on the anchor name.
	 *
	 * Runs after the watch above has rebound to the CURRENT step's anchor, so the disappearance this causes
	 * is never read as the current control vanishing under the tour.
	 *
	 * It does NOT only run on a step change: `trackError`'s identity turns over whenever an analytics
	 * provider ancestor re-renders, so this fires at arbitrary DOM moments — including while a surface is
	 * still transitioning. Everything below therefore has to be safe to run at any instant, not only at the
	 * tidy moment joyride reports a step.
	 *
	 * An absent current anchor DEFERS the decision rather than dropping it. Bailing outright was safe but
	 * one-sided: a step whose control mounts a moment later would then keep a revealed drawer over it for
	 * the rest of the tour, with nothing left to re-ask. So absence arms an observer and re-runs once the
	 * control is really there.
	 */
	// biome-ignore lint/correctness/useExhaustiveDependencies: `dismissRetry` is a re-run trigger, never read — the observer below bumps it once the absent anchor mounts, which is the whole point of deferring rather than dropping the decision.
	useEffect(() => {
		if (!isRunning || walkthrough === null) return;
		// One unwind at a time. Each runs up to MAX_DISMISS_DEPTH layers with a sleep between them, so a
		// re-fire mid-flight would dispatch a second Escape into a surface the first one is still closing.
		if (dismissingRef.current) return;
		const failed = (error: unknown) => trackError("annie_walkthrough_dismiss_failed", { topic_id: walkthrough.topicId, message: String(error) });
		let stranded: string[];
		let awaitingAnchor: string | null = null;
		try {
			const currentAnchor = (steps[stepIndex]?.data as WalkthroughStepData | undefined)?.anchor;
			if (currentAnchor === undefined) return;
			const here = overlaySurfaceOf(currentAnchor);
			// The current control isn't on screen, so there is no evidence the tour has LEFT anything — and
			// acting on that guess is what closes the panel the step is standing in. Re-ask when it arrives.
			if (here === undefined) {
				awaitingAnchor = currentAnchor;
				stranded = [];
			} else if (here?.matches(MODAL_SURFACE_SELECTOR) === true) {
				// The tour is standing INSIDE a modal, which is stacked above whatever revealed it — so nothing
				// behind it can cover this step's control, and covering a LATER control is the only thing this
				// unwind exists to prevent. Closing anyway is actively destructive once surfaces nest: the
				// broadcast composer is portaled out of the admin side panel that opened it, but a closed
				// `UpdatesPanel` drops its body — `StaffCommunication` and the composer with it — so
				// dismissing the drawer here pulls the current step's own control out of the DOM and the vanish
				// watch ends the tour mid-flow. A modal check rather than a computed z-index comparison: the
				// stacking answer is the same and this one can be read. Wider than "modal" suggests, and
				// deliberately so: `MuiMenu` and `MuiPopover` roots carry `MuiModal-root` too, so a step standing
				// inside a MENU also suppresses the unwind. No shipped tour opens a second surface from inside a
				// menu, so nothing is affected today — but a future one that does would keep its menu open until
				// the end rather than mid-tour. `stop()` still unwinds everything at the end, so nothing is left
				// open — it is only deferred to then.
				return;
			} else {
				stranded = steps
					.slice(0, stepIndex)
					.map((step) => (step.data as WalkthroughStepData | undefined)?.revealAnchor)
					.filter((anchor): anchor is string => {
						if (typeof anchor !== "string") return false;
						const surface = overlaySurfaceOf(anchor);
						// Already gone is nothing to dismiss; only a surface still open and genuinely elsewhere.
						return surface !== undefined && surface !== here;
					});
			}
		} catch (error) {
			// `anchorSelector` throws on a malformed name. Synchronous, so it would otherwise escape into
			// React's tree rather than the tracked path the dismissal itself reports through.
			failed(error);
			return;
		}
		if (awaitingAnchor !== null) {
			const anchor = awaitingAnchor;
			const observer = new MutationObserver(() => {
				if (!isAnchorVisible(anchor)) return;
				observer.disconnect();
				setDismissRetry((tick) => tick + 1);
			});
			observer.observe(document.body, ANCHOR_ARRIVAL_MUTATIONS);
			return () => observer.disconnect();
		}
		if (stranded.length === 0) return;
		dismissingRef.current = true;
		// Abandoned when this run is no longer the live one. An unwind can be in flight for over three
		// seconds, and the tour can end, be replaced, or unmount inside that window — after which a late
		// Escape lands on whatever the user has opened since, with the tour's own Escape guard already gone.
		let abandoned = false;
		dismissRevealedAnchors(stranded, () => abandoned)
			.catch(failed)
			.finally(() => {
				dismissingRef.current = false;
			});
		return () => {
			abandoned = true;
		};
	}, [isRunning, steps, stepIndex, walkthrough, trackError, dismissRetry]);

	const trackWrongClick = useCallback(
		(step: number) => {
			if (walkthrough !== null) track("annie_walkthrough_wrong_click", { topic_id: walkthrough.topicId, step });
		},
		[walkthrough, track],
	);
	const tourGuide = useWalkthroughTourGuide({ guide, isRunning, steps, stepIndex, onWrongClick: trackWrongClick });

	const shownStepRef = useRef<number | null>(null);
	// A new tour starts counting afresh, so its first step is tracked even if the last one ended on step 0.
	// biome-ignore lint/correctness/useExhaustiveDependencies: `walkthrough` is the reset trigger, never read — each new tour must clear the last one's step.
	useEffect(() => {
		shownStepRef.current = null;
	}, [walkthrough]);

	const handleEvent = useCallback(
		(data: EventData, eventControls: Controls) => {
			if (walkthrough === null) return;
			if (data.type === "error:target_not_found") {
				// An anchor drifted, or the control isn't rendered here. No build-time check can prove a target
				// renders, so this is how we find out.
				track("annie_walkthrough_target_missing", { topic_id: walkthrough.topicId, step: data.index });
				return;
			}
			if (data.type === "tooltip") {
				setControls(eventControls);
				setStepIndex(data.index);
				// Once per arrival: a re-anchored step (its control replaced under it) reports `tooltip` again.
				if (shownStepRef.current !== data.index) {
					shownStepRef.current = data.index;
					tourGuide.onStepShown();
					track("annie_walkthrough_step_shown", { topic_id: walkthrough.topicId, step: data.index });
				}
				return;
			}
			if (data.type === "tour:end") {
				track("annie_walkthrough_ended", { topic_id: walkthrough.topicId, status: data.status, step: data.index });
				stop();
			}
		},
		[walkthrough, stop, track, tourGuide.onStepShown],
	);

	if (walkthrough === null || !isRunning || steps.length === 0) return null;

	return (
		<Suspense fallback={null}>
			<WalkthroughTooltipGuideContext.Provider value={tourGuide.tooltipGuide}>
				<WalkthroughNextOpensContext.Provider value={onNextOpens}>
					<Joyride
						run={isRunning}
						steps={steps}
						continuous
						scrollToFirstStep
						tooltipComponent={WalkthroughTooltip}
						onEvent={handleEvent}
						// joyride fades the tooltip in over 300ms (its default `floater` style) while mounting it at
						// opacity 0 ALREADY in its final place — there is no motion to carry, just a delay. The spotlight
						// has meanwhile jumped to the next control, so every Next left ~300ms of the next control lit
						// with no tooltip anywhere, which reads as the tooltip lagging a step behind. Measured, not
						// guessed: the floater's opacity ramps 0 → 1 across ~15 samples at 20ms.
						styles={{ floater: { transition: "none" } }}
						options={{
							// The user is already looking at the control, so a beacon to click first is pure friction.
							skipBeacon: true,
							// The same cap `joyrideSteps` puts on every step it builds, so a step that ever reaches joyride
							// without going through it inherits the 400ms blackout budget rather than joyride's own 1s.
							// Per-step wins over this (`targetWaitTimeout` is one of joyride's `optionFieldNames`, and
							// `getMergedStep` merges the step last), so the two must agree or this reads as the real one.
							targetWaitTimeout: MISSING_TARGET_WAIT_MS,
							zIndex: WALKTHROUGH_Z_INDEX,
							// Inert on purpose: v3 offers only 'close' and 'next' here and BOTH advance a step
							// (`controls.close` sets index + 1), so a stray click would skip content rather than exit.
							// The ways out are the tooltip's ✕ and Escape.
							overlayClickAction: false,
							// Handled by the capture listener above, so it isn't left to race with the panel's.
							dismissKeyAction: false,
						}}
					/>
				</WalkthroughNextOpensContext.Provider>
			</WalkthroughTooltipGuideContext.Provider>
		</Suspense>
	);
};
